import React, { useState, useEffect, useCallback } from 'react'
import Image from 'next/image'

interface ExitPopupProps {
  image: string
  heading: string
  body: string
  successHeading: string
  successBody: string
  errorHeading: string
  errorBody: string
  alt: string
  buttonText: string
  tracking: string
  eventLabel: string
  cookieName: string
  mobile: boolean
  formAction: string
}

interface LocalStorageItem {
  value: string
  expiry: string
}

const setLocalStorage = (name: string, value: string, days: number) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  const item: LocalStorageItem = {
    value,
    expiry: expires.toISOString(),
  }
  localStorage.setItem(name, JSON.stringify(item))
}

const getLocalStorage = (name: string): string | null => {
  const itemStr = localStorage.getItem(name)
  if (!itemStr) {
    return null
  }
  const item: LocalStorageItem = JSON.parse(itemStr) as LocalStorageItem
  const now = new Date()
  if (now.toISOString() > item.expiry) {
    localStorage.removeItem(name)
    return null
  }
  return item.value
}

const validateEmail = (email: string) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(String(email).toLowerCase())
}

const ExitPopup: React.FC<ExitPopupProps> = ({
  image,
  heading,
  body,
  successHeading,
  successBody,
  errorHeading,
  errorBody,
  alt,
  buttonText,
  tracking,
  eventLabel,
  cookieName,
  mobile,
  formAction,
}) => {
  const [visible, setVisible] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [formStatus, setFormStatus] = useState('')
  const [consent, setConsent] = useState(false)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')

  const showPopup = useCallback(() => {
    if (!getLocalStorage(cookieName)) {
      setVisible(true)
      setLocalStorage(cookieName, 'true', 30)

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'exitBannerPopup',
          'ExitBanner-Popup': {
            eventLabel,
            eventAction: 'ExitPopup-Viewed',
          },
        })
      }
    }
  }, [cookieName, eventLabel])

  useEffect(() => {
    const handleMouseOut = (e: MouseEvent) => {
      if (!e.relatedTarget && e.clientY < 10) {
        showPopup()
      }
    }

    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.body.scrollHeight) {
        showPopup()
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setVisible(false)
      }
    }

    if (mobile) {
      document.addEventListener('scroll', handleScroll)
    } else {
      document.addEventListener('mouseout', handleMouseOut)
      document.addEventListener('scroll', handleScroll)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mouseout', handleMouseOut)
      document.removeEventListener('scroll', handleScroll)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showPopup, mobile])

  const handleClose = () => {
    setVisible(false)
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    const formData = new FormData(e.currentTarget)
    formData.append('id', `RE-NWS-ARTICLES-${email}`)
    formData.append('blake', '0')

    fetch(`${formAction}${tracking}`, {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        if (response.ok) {
          setFormStatus('success')
          setFormSubmitted(true)
          window.location.href = '/reading-worksheets/'
        } else {
          setFormStatus('error')
          setFormSubmitted(true)
        }
      })
      .catch(() => {
        setFormStatus('error')
        setFormSubmitted(true)
      })
  }

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConsent(e.target.checked)
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (!validateEmail(e.target.value)) {
      setEmailError('Please enter a valid email address')
    } else {
      setEmailError('')
    }
  }

  return (
    <>
      {visible && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75"
          onClick={handleClose}
        >
          <div
            className="m-4 flex max-h-screen w-full max-w-xs transform flex-col overflow-auto rounded-lg bg-white p-4 shadow-xl transition-all"
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <button
              type="button"
              className="mb-4 self-end text-xl text-gray-700"
              onClick={handleClose}
            >
              ×
            </button>
            <div className="mb-4 flex-shrink-0">
              {!imageError ? (
                <div className="relative h-48 w-full">
                  <Image
                    src={image}
                    alt={alt}
                    layout="fill"
                    objectFit="contain"
                    onError={() => {
                      setImageError(true)
                    }}
                  />
                </div>
              ) : (
                <div className="flex h-48 w-full items-center justify-center bg-gray-100 text-gray-500">
                  Image not available
                </div>
              )}
            </div>
            <div className="flex-grow text-center">
              <h2 className="mb-4 text-2xl font-semibold">{heading}</h2>
              <p className="mb-4 text-base">{body}</p>
              {!formSubmitted ? (
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    required
                    className="w-full rounded-lg border border-gray-300 px-4 py-2"
                    value={email}
                    onChange={handleEmailChange}
                  />
                  {emailError && (
                    <p className="mt-1 text-xs text-red-500">{emailError}</p>
                  )}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="consent"
                      name="consent"
                      required
                      checked={consent}
                      onChange={handleConsentChange}
                      className="mr-2"
                    />
                    <label htmlFor="consent" className="text-sm">
                      By registering you agree to our{' '}
                      <a
                        target="_blank"
                        href="/terms/"
                        className="text-blue-600 underline"
                      >
                        Terms
                      </a>{' '}
                      and{' '}
                      <a
                        target="_blank"
                        href="/privacy/"
                        className="text-blue-600 underline"
                      >
                        Privacy Policy
                      </a>
                      .
                    </label>
                  </div>
                  <button
                    type="submit"
                    className={`w-full rounded-lg py-2 font-bold ${
                      consent
                        ? 'bg-pink-600 text-white hover:bg-pink-700'
                        : 'cursor-not-allowed bg-gray-400 text-gray-700'
                    }`}
                    disabled={!consent || !!emailError}
                  >
                    {buttonText}
                  </button>
                </form>
              ) : formStatus === 'success' ? (
                <div>
                  <h2 className="mb-4 text-2xl font-semibold">
                    {successHeading}
                  </h2>
                  <p>{successBody}</p>
                </div>
              ) : (
                <div>
                  <h2 className="mb-4 text-2xl font-semibold">
                    {errorHeading}
                  </h2>
                  <p dangerouslySetInnerHTML={{ __html: errorBody }}></p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ExitPopup
